import { css } from '@emotion/react';
import React from 'react';
import { AiProfileInfo, OnboardingAiProfileType } from '@/customTypes/chapter';
import StoryGameCharacterList from './StoryGameCharacterList';

import { AiCharacterImageType, MainCharacter } from '@/customTypes/storygame';
import theme from '@/styles/theme';
import StoryGamePreviewCharacterName from './StoryGamePreviewCharacterName';
import StoryGameOnboardingAlert, {
  IStoryGameOnboardingAlert,
} from '@/components/storyGame/popup/StoryGameOnboardingAlert';
import useTranslation from 'next-translate/useTranslation';

interface StoryGameAiImagePreviewProps {
  setAiProfileType: (type: OnboardingAiProfileType) => void;
  onNext: () => void;
  onChangeAiProfile: (newAiProfileInfo: AiProfileInfo) => void;
  onGetAiCharacterSessionId: () => Promise<void>;

  onGetAiCharacterImageList: (chrId?: number) => Promise<void>;
  aiCharacterImageList?: AiCharacterImageType[]; //profileImage는 default캐릭터에만 있음

  onClickStoryGameCharacter: (character: AiProfileInfo) => void;
  selectedCharacter?: MainCharacter;

  onCreateCharacter: () => Promise<void>;
  aiProfileInfo?: AiProfileInfo;
  aiProfileInfoArray?: AiProfileInfo[];
  addOpenedStorygameIds: () => void;
  openedStorygameIds?: string;
  isShowPromotionStorygame: boolean;
  setIsShowPromotionStorygame: (value: boolean) => void;
  bg?: string;
  setIsStoryGameEdit: (value: boolean) => void;
  selectPreviousCharacter: () => void;
  selectNextCharacter: () => void;
  storygameAlertData: IStoryGameOnboardingAlert;
  setStorygameAlertData: (data: IStoryGameOnboardingAlert) => void;
  removeSessionId: () => void;
}

const StoryGameAiImagePreview = ({
  onNext,
  onChangeAiProfile,
  aiCharacterImageList,
  onClickStoryGameCharacter,
  selectedCharacter,
  aiProfileInfo,
  aiProfileInfoArray,
  setIsStoryGameEdit,
  bg,
  selectPreviousCharacter,
  selectNextCharacter,
  storygameAlertData,
  setStorygameAlertData,
  removeSessionId,
}: StoryGameAiImagePreviewProps) => {
  const { t } = useTranslation();
  return (
    <div css={styles()}>
      <div className="bg" />
      <div className="top-container">
        {/* <div className="top-left">
          <HomeIcon />
        </div> */}
        <div className="top-title">
          {t('storygame_ai_onboarding_screen_description_start_this_character')}
        </div>
      </div>

      <div className="container">
        <div className="side-container">
          <div className="character-list-setup">
            <StoryGameCharacterList
              characterList={aiProfileInfoArray}
              selectedCharacter={selectedCharacter}
              onClickStoryGameCharacter={onClickStoryGameCharacter}
            />
          </div>
        </div>
        <div className="image-container">
          {aiProfileInfo?.portraitFile?.link ? (
            <img
              src={aiProfileInfo?.portraitFile?.link}
              className="ai_character"
              alt="ai_character"
            />
          ) : (
            <div className="ai_character">
              <div css={loadingCss('4rem')}>
                <div className="loading" />
              </div>
            </div>
          )}

          <div className="bottom-container">
            {selectedCharacter && aiProfileInfo && (
              <StoryGamePreviewCharacterName
                characterImage={
                  aiProfileInfo?.profileFile ??
                  aiProfileInfo?.defaultProfileImageFile?.link
                }
                firstName={aiProfileInfo?.firstName ?? ''}
                lastName={aiProfileInfo?.lastName ?? ''}
                displayName={selectedCharacter.displayName}
                onChangeAiProfile={onChangeAiProfile}
                onProfileEdit={function (): void {
                  setIsStoryGameEdit(true);
                }}
                isDefault={
                  !!aiCharacterImageList?.find((characterImage) => {
                    return (
                      characterImage.profileImage?.fileId ===
                      aiProfileInfo?.defaultProfileImageFile?.fileId
                    );
                  })?.isDefault
                }
                onNext={selectNextCharacter}
                onPrevious={selectPreviousCharacter}
              />
            )}
            <div
              className="start-button"
              onClick={() => {
                setStorygameAlertData({
                  title: t(
                    'storygame_ai_onboarding_popup_description_alert_complete_and_start',
                  ),
                  subtitle: t(
                    'storygame_ai_onboarding_popup_description_alert_info_cannot_change',
                  ),
                  onCancel: () => {
                    setStorygameAlertData({
                      ...storygameAlertData,
                      isVisible: false,
                    });
                  },
                  confirmText: t('common_screen_button_start'),
                  onConfirm: () => {
                    setStorygameAlertData({
                      ...storygameAlertData,
                      isVisible: false,
                    });
                    removeSessionId();
                    onNext();
                  },
                  isVisible: true,
                });
              }}
            >
              {t('storygame_ai_onboarding_screen_button_start_game')}
            </div>
          </div>
        </div>
      </div>
      <StoryGameOnboardingAlert storygameAlertData={storygameAlertData} />
    </div>
  );
};

const styles = () => css`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #0000001a;
    backdrop-filter: blur(8px);
  }
  .top-container {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;

    padding: 12px;
    height: 72px;
    justify-content: center;

    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    z-index: 1;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.25) 0%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  .top-container .top-left {
    position: absolute;
    left: 12px;
    display: flex;
    align-items: center;
  }

  .start-button {
    color: #fff;
    font-size: 18px;
    font-weight: 700;

    display: flex;
    padding: 18px 0px;
    min-height: 48px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    background: #753bbd;
    cursor: pointer;
    width: 50%;
  }

  .top-container .top-title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 500;
    position: absolute;
  }

  .container {
    width: 100%;
    flex: 1;

    position: relative;
    display: flex;

    flex-direction: row;
    overflow: hidden;
    max-width: 1920px;
  }
  .image-container {
    height: 100%;

    width: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: end;
    // border-radius: 0.625rem;
    // border: 1px solid rgba(255, 255, 255, 0.25);

    @media screen and (max-width: 1024px) {
      min-width: 480px;
    }
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  .container .ai_character {
    display: flex;
    justify-content: center;
    object-fit: contain;

    height: 85vh;

    @media screen and (max-width: 1024px) {
      min-width: 480px;
      min-height: 480px;
    }
  }

  .bottom-container {
    position: absolute;
    bottom: 10%;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 12px;
  }

  .side-container {
    display: flex;
    flex-direction: column;

    margin: 20px 0px;
    align-items: center;
    justify-content: center;

    padding: 0px 0px 0px 48px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    flex-direction: column;
    z-index: 1;
  }
  .character-list-setup {
    position: relative;

    color: white;
    height: 580px;
    display: flex;
    flex-direction: column;
  }

  .side-container-right {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 20px 0px;
    align-items: end;
    justify-content: center;

    padding: 0px 48px 0px 0px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    flex-direction: column;
  }

  .ai-character-setup {
    position: relative;
    padding: 20px;
    color: white;
    height: 580px;
    display: flex;
    flex-direction: column;
    justify-content: space-between; /* 내부 요소들을 위아래로 분산 배치 */

    border-radius: 12px;
    background: rgba(0, 0, 0, 0.75);
  }

  .title {
    text-align: center;
    margin-bottom: 20px;
    color: #fff;

    font-size: 18px;
    font-weight: 500;
  }

  .ai-image-section {
    width: 100px;
    height: 100px;
    border: 1.5px dashed rgba(255, 255, 255, 0.5);
    border-radius: 8px;
    padding: 14px 0px;
    text-align: center;

    font-size: 14px;
    justify-content: center;
    align-items: center;
    display: flex;
    flex-direction: column;

    background: linear-gradient(
      136deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0) 97.87%
    );

    backdrop-filter: blur(8px);
  }

  .ai-icon {
    width: 24px;
    height: 24px;
    font-size: 24px;
    margin-bottom: 10px;
  }

  .ai-text {
    margin: 0;
  }
  .ai-character-list-container {
    width: 100%;
    overflow-y: auto;
    max-height: 400px; /* 또는 원하는 높이 */
    margin-bottom: 8px;
  }
  .ai-character-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 0;
  }

  .ai-character-section {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    border-radius: 10px;
    border: solid 1px transparent;
    display: flex;
    align-items: center;
    position: relative;
    justify-content: center;
    cursor: pointer;
    transition: border-color 0.3s ease;
    border-color: #ffffff80;
    opacity: 0.5;
  }

  .ai-character-section.selected {
    border-color: #9457eb;
    opacity: 1;

    overflow: hidden;
  }

  .ai-character-image {
    width: 100%;
    height: 100%;
    border-radius: 5px;
    object-fit: contain;
  }
  .overlay-bg {
    width: 100%;
    height: 100%;
    background-color: #9457eb40;
    position: absolute;
    top: 0px;
    left: 0px;
  }

  .status-info {
    position: absolute;
    top: 0px;
    left: 0px;
    background-color: #9457eb;
    border-radius: 5px 0px 0px 0px;
    padding: 2px 6px;
  }

  .ai-character-status {
    margin: 0;
    font-size: 13px;
    color: #ddd;
  }

  .info-icon {
    font-size: 16px;
    position: absolute;
    top: 6px;
    right: 6px;
  }

  .share-icon {
    text-align: center;
    font-size: 24px;
    margin-top: auto; /* 상단 여백을 자동으로 조절하여 하단에 위치 */
    padding-top: 20px; /* 위쪽 여백 추가 */
  }

  .loading-text1 {
    margin-top: 5px;
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    text-align: center;
    color: #ffffff;
  }
  .loading-text2 {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;
    color: #ffffffbf;
  }
  // @media screen and (max-width: 1024px) {
  //   width: 1024px;
  // }
`;

const loadingCss = (size: string) => css`
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  .loading {
    width: ${size ? size : '8em'};
    height: ${size ? size : '8em'};
    border-radius: 50%;
    border: 5px solid ${theme.colors.primary500};
    border-top: 3px solid transparent;
    animation: spin 1s linear infinite;
    @media screen and (max-width: ${theme.devices.mobile}) {
      width: ${size ? size : '4em'};
      height: ${size ? size : '4em'};
      border-width: 3px;
    }
  }
`;

export default StoryGameAiImagePreview;
